import { store } from '../../store';
import { setToken, updateCurrentUser, logout as logoutAction } from '../../store/slices/authSlice';
import { env } from '../../config/env';
import axios from 'axios';
import { getAxiosErrorMessage } from '../../utils/getAxiosErrorMessage';

const apiUrl = env.apiUrl;

export const authService = {
  async login(email, password) {
    const body = new URLSearchParams({
      grant_type: 'password',
      username: email,
      password,
      scope: 'email profile roles offline_access',
    });

    try {
      const tokenResponse = await axios.post(`${apiUrl}/auth/token`, body.toString(), {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });

      const tokenData = tokenResponse.data;

      store.dispatch(
        setToken({
          token: tokenData.access_token,
          refreshToken: tokenData.refresh_token || null,
          expiresAt: tokenData.expires_in
            ? Date.now() + tokenData.expires_in * 1000
            : null,
        })
      );

      // Fetch user info using the new token
      const userInfoResponse = await axios.get(`${apiUrl}/auth/user-info`, {
        headers: {
          Authorization: `Bearer ${tokenData.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      const userData = userInfoResponse.data;
      store.dispatch(updateCurrentUser(userData));

      const user = {
        id: userData.sub,
        firstName: userData.given_name,
        lastName: userData.family_name,
        name: `${userData.given_name} ${userData.family_name}`,
        email: userData.email,
        role: userData.role,
      };

      return {
        success: true,
        user,
        token: tokenData.access_token,
      };
    } catch (err) {
      throw new Error(
        getAxiosErrorMessage(err, 'Login failed. Please check your credentials.')
      );
    }
  },

  loginWithGoogle(returnUrl) {
    // Redirect to the backend challenge endpoint — the backend handles the full OAuth flow
    const url = returnUrl || window.location.href;
    window.location.href = `${apiUrl}/auth/challenge/google?returnUrl=${encodeURIComponent(url)}`;
  },

  loginWithMicrosoft(returnUrl) {
    // Redirect to the backend challenge endpoint — the backend handles the full OAuth flow
    const url = returnUrl || window.location.href;
    window.location.href = `${apiUrl}/auth/challenge/microsoft?returnUrl=${encodeURIComponent(url)}`;
  },

  async exchangeExternalLogin() {
    // Called after the backend callback redirects back with ?external_login=success.
    // The backend set an HttpOnly cookie scoped to /api/auth/token.
    // Exchange it for tokens via the external-login grant type.
    const body = new URLSearchParams({
      grant_type: 'external-login',
      scope: 'email profile roles offline_access',
    });

    try {
      const tokenResponse = await axios.post(`${apiUrl}/auth/token`, body.toString(), {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        withCredentials: true,
      });

      const tokenData = tokenResponse.data;

      store.dispatch(
        setToken({
          token: tokenData.access_token,
          refreshToken: tokenData.refresh_token || null,
          expiresAt: tokenData.expires_in
            ? Date.now() + tokenData.expires_in * 1000
            : null,
        })
      );

      const userInfoResponse = await axios.get(`${apiUrl}/auth/user-info`, {
        headers: {
          Authorization: `Bearer ${tokenData.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      const userData = userInfoResponse.data;
      store.dispatch(updateCurrentUser(userData));

      return {
        success: true,
        user: {
          id: userData.sub,
          firstName: userData.given_name,
          lastName: userData.family_name,
          name: `${userData.given_name} ${userData.family_name}`,
          email: userData.email,
          role: userData.role,
        },
        token: tokenData.access_token,
      };
    } catch (err) {
      throw new Error(getAxiosErrorMessage(err, 'External login failed.'));
    }
  },

  async exchangeAccountActivation() {
    // Called after ConfirmAccountAsync succeeds. The backend set an HttpOnly cookie
    // scoped to /api/auth/token. Exchange it for tokens via the account-activation grant.
    const body = new URLSearchParams({
      grant_type: 'account-activation',
      scope: 'email profile roles offline_access',
    });

    try {
      const tokenResponse = await axios.post(`${apiUrl}/auth/token`, body.toString(), {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        withCredentials: true,
      });

      const tokenData = tokenResponse.data;

      store.dispatch(
        setToken({
          token: tokenData.access_token,
          refreshToken: tokenData.refresh_token || null,
          expiresAt: tokenData.expires_in
            ? Date.now() + tokenData.expires_in * 1000
            : null,
        })
      );

      const userInfoResponse = await axios.get(`${apiUrl}/auth/user-info`, {
        headers: {
          Authorization: `Bearer ${tokenData.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      const userData = userInfoResponse.data;
      store.dispatch(updateCurrentUser(userData));

      return {
        success: true,
        user: {
          id: userData.sub,
          firstName: userData.given_name,
          lastName: userData.family_name,
          name: `${userData.given_name} ${userData.family_name}`,
          email: userData.email,
          role: userData.role,
        },
        token: tokenData.access_token,
      };
    } catch (err) {
      throw new Error(getAxiosErrorMessage(err, 'Could not complete sign-in.'));
    }
  },

  logout() {
    // Fire the backend logout (best-effort) then clear local state
    try {
      const token = store.getState().auth.token;
      if (token) {
        axios.put(`${apiUrl}/auth/logout`, null, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        }).catch(() => {});
      }
    } catch {
      // Ignore errors during logout
    }
    store.dispatch(logoutAction());
  },

  getCurrentUser() {
    const state = store.getState();
    return state.auth.user || null;
  },

  isAuthenticated() {
    const state = store.getState();
    return state.auth.isAuthenticated;
  },

  getToken() {
    const state = store.getState();
    return state.auth.token || null;
  }
};
